import {
  type BuildingModelInput,
  checksByLine,
  flagKey,
  groupFlags,
  indexElements,
  isFinished,
  lineCheckState,
  lineWeights,
  reviewOrder,
  reviewProgress,
  settledByKey,
  type TakeoffLine,
} from "@lumberset/estimating";
import {
  type AssumptionWork,
  assumptionWork,
  type CorrectionReason,
  CorrectionsEditor,
  Drawing,
  type DrawingHandle,
  drawingKey,
  elementFocus,
  elementOrder,
  elementStates,
  elementsByPage,
  type FlagItem,
  type Focus,
  flagFocus,
  focusKey,
  type LineInfo,
  type ListIndex,
  lineFocus,
  listTarget,
  type MeasureWork,
  needsYouWork,
  nextWork,
  pagesOf,
  type ReviewKit,
  ReviewProvider,
  reveal,
  SheetIndexProvider,
  useDrawingMemory,
  useModelDraft,
  useSheetLabel,
  workOrder,
} from "@lumberset/takeoff-ui";
import { Button, Container, cn, Drawer, EmptyState, toast } from "@lumberset/ui";
import { ExclamationCircle, Photo } from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Crumbs } from "../../../components/crumbs";
import { type ReviewDetail, reviewQuery } from "../../../lib/queries";
import { ApprovePrompt, CloseOvertakenPrompt, KeysPrompt } from "./-workspace/dialogs";
import { ReviewHeader } from "./-workspace/header";
import { useActiveTime, useMediaQuery, useSplit, useSteadySheets } from "./-workspace/hooks";
import { type ListFilter, type ListView, ReviewList } from "./-workspace/review-list";
import { type ReviewWork, useReviewWork } from "./-workspace/use-review-work";

export const Route = createFileRoute("/_app/reviews/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(reviewQuery(params.id)),
  component: ReviewScreen,
});

type User = { id: string; name?: string | null; role?: string | null };
type Revision = NonNullable<ReviewDetail["revision"]>;

/** Wide enough for the drawing beside the list. Narrower screens pull the drawing up in a drawer. */
const DESKTOP = "(min-width: 1280px)";

function ReviewScreen() {
  const { id } = Route.useParams();
  const { user } = Route.useRouteContext();
  const { data } = useSuspenseQuery(reviewQuery(id));
  const work = useReviewWork(id, user.id);
  // Time working on this screen, paused while the tab is hidden or idle, is what approving records.
  const time = useActiveTime();

  const trail = (
    <Crumbs trail={[{ label: "Review queue", to: "/" }, { label: data.projectName }]} />
  );
  if (!data.revision) {
    return (
      <>
        {trail}
        <Container className="p-0">
          <EmptyState
            icon={<ExclamationCircle />}
            title="No takeoff to review"
            description={
              data.run.error ?? "Reading has not produced a Building Model for this run."
            }
          />
        </Container>
      </>
    );
  }
  return (
    <>
      {trail}
      <SheetIndexProvider sheets={data.sheets}>
        <Workspace data={data} revision={data.revision} user={user} work={work} time={time} />
      </SheetIndexProvider>
    </>
  );
}

/**
 * The Reviewer's workspace: the drawing, where the work happens, beside one
 * list of everything to do. What needs the Reviewer comes first, then the
 * job-wide assumptions, the drawing notes and every line to check off, each
 * fixed where it is seen; or the same work Sheet by Sheet. On a phone the
 * drawing pulls up as a drawer.
 */
function Workspace({
  data,
  revision,
  user,
  work,
  time,
}: {
  data: ReviewDetail;
  revision: Revision;
  user: User;
  work: ReviewWork;
  time: { minutes: number; seconds: () => number };
}) {
  const { review } = data;
  const takeoff = revision.takeoff;
  const model = revision.buildingModel as BuildingModelInput;
  const standards = revision.standards;
  const desktop = useMediaQuery(DESKTOP);
  const sheets = useSteadySheets(data.sheets);
  const labelFor = useSheetLabel();

  // Approved, or closed because a newer takeoff was released: nothing more to do either way.
  const finished = isFinished(review.status);
  // Sent back to be read again: the Review waits for the new reading, and nothing is saved
  // meanwhile, nor after a reading that failed until it is read again.
  const reading = ["queued", "running", "failed"].includes(data.run.status);
  const isAdmin = user.role === "admin";
  const staff = user.role === "reviewer" || isAdmin;
  const canWork = staff && !finished && !reading && (review.assignedTo === user.id || isAdmin);
  const canClaim = staff && !finished && !reading && review.assignedTo !== user.id;
  // Closing a review a newer takeoff overtook is work on it too: the claimer, an Admin, or anyone while unclaimed.
  const canClose =
    staff && !finished && (isAdmin || review.assignedTo === null || review.assignedTo === user.id);
  const people = useMemo(
    () =>
      new Map([
        // Whoever is working now, named even before the Review is read again.
        [user.id, user.name ?? "you"],
        ...data.people.map((person) => [person.id, person.name] as const),
      ]),
    [data.people, user.id, user.name],
  );

  /* ---- What the list knows ---- */

  const index = useMemo(() => indexElements(model), [model]);
  const settled = useMemo(() => settledByKey(review.settledFlags), [review.settledFlags]);
  const checks = useMemo(() => checksByLine(review.checkedLines), [review.checkedLines]);
  const groups = useMemo(() => groupFlags(takeoff.flags, model), [takeoff.flags, model]);
  const needs = useMemo(() => needsYouWork(groups.needsYou, settled), [groups, settled]);
  const assumptions = useMemo(() => assumptionWork(groups.assumptions, settled), [groups, settled]);
  const notes = useMemo<FlagItem[]>(
    () =>
      groups.notes.map((flag) => {
        const key = flagKey(flag);
        return { flag, key, settled: settled.get(key) };
      }),
    [groups, settled],
  );
  const progress = useMemo(
    () =>
      finished && data.progress
        ? data.progress
        : reviewProgress(takeoff, review.checkedLines, review.settledFlags),
    [finished, data.progress, takeoff, review.checkedLines, review.settledFlags],
  );
  const info = useMemo(() => {
    const needsBy = new Map<string, FlagItem[]>();
    for (const item of needs.flatMap((group) => group.open)) {
      for (const id of item.flag.elementIds) needsBy.set(id, [...(needsBy.get(id) ?? []), item]);
    }
    const assumedBy = new Map<string, AssumptionWork[]>();
    for (const row of assumptions) {
      for (const item of row.open) {
        for (const id of item.flag.elementIds) {
          const known = assumedBy.get(id) ?? [];
          if (!known.includes(row)) assumedBy.set(id, [...known, row]);
        }
      }
    }
    return new Map<string, LineInfo>(
      takeoff.lines.map((line) => {
        const flags = new Set<FlagItem>();
        const assumed = new Set<AssumptionWork>();
        for (const id of line.elementIds) {
          for (const item of needsBy.get(id) ?? []) flags.add(item);
          for (const row of assumedBy.get(id) ?? []) assumed.add(row);
        }
        return [
          line.id,
          {
            state: lineCheckState(line, checks),
            flags: [...flags],
            assumptions: [...assumed],
            pages: pagesOf(elementOrder(line), index),
          },
        ];
      }),
    );
  }, [takeoff.lines, needs, assumptions, checks, index]);
  // The order the Materials are listed in: by section and place, or the costliest likely errors first.
  const [riskFirst, setRiskFirst] = useState(false);
  const weights = useMemo(() => lineWeights(takeoff.lines), [takeoff.lines]);
  const listedLines = useMemo(
    () => (riskFirst ? reviewOrder(takeoff.lines, weights) : takeoff.lines),
    [riskFirst, takeoff.lines, weights],
  );
  /** Where each Element is dealt with in the list, for a mark clicked on the drawing. */
  const listIndex = useMemo<ListIndex>(
    () => ({
      needs: needs.flatMap((group) =>
        group.open[0]
          ? [
              {
                row: focusKey.flag(group.open[0].key),
                elementIds: group.open.flatMap((item) => item.flag.elementIds),
              },
            ]
          : [],
      ),
      assumptions: assumptions
        .filter((row) => row.open.length > 0)
        .map((row) => ({
          row: focusKey.assumption(row.assumption.topic),
          elementIds: row.open.flatMap((item) => item.flag.elementIds),
        })),
      notes: notes
        .filter((item) => !item.settled)
        .map((item) => ({ row: focusKey.flag(item.key), elementIds: item.flag.elementIds })),
      lines: listedLines,
    }),
    [needs, assumptions, notes, listedLines],
  );
  /**
   * How far Review has got with each Element: the dots on the drawing say it.
   * Job-wide assumptions are left out: Species and grade alone names nearly
   * every Element, and each line lists the ones on it as still open.
   */
  const states = useMemo(
    () =>
      elementStates({
        flags: [...needs.flatMap((group) => group.items), ...notes].map((item) => ({
          elementIds: item.flag.elementIds,
          open: !item.settled,
        })),
        lines: takeoff.lines.map((line) => ({
          elementIds: line.elementIds,
          checked: info.get(line.id)?.state === "checked",
        })),
      }),
    [needs, notes, takeoff.lines, info],
  );
  const pagesRead = useMemo(() => {
    const byPage = elementsByPage(index);
    return sheets.filter((sheet) => (byPage.get(sheet.page)?.length ?? 0) > 0).map((s) => s.page);
  }, [index, sheets]);
  const sheetProgress = {
    checked: review.checkedSheets.filter((check) => pagesRead.includes(check.page)).length,
    total: pagesRead.length,
  };

  /* ---- Where the Reviewer is ---- */

  const [view, setView] = useState<ListView>("work");
  const [filter, setFilter] = useState<ListFilter>("all");
  const [focus, setFocusState] = useState<Focus | null>(null);
  const shown = useRef(0);
  /** Chooses something; each request is stamped, so asking again for the same one still shows it. */
  const setFocus = (next: Focus | null) => {
    shown.current += 1;
    setFocusState(next ? { ...next, seq: shown.current } : null);
  };
  const [expanded, setExpanded] = useState<{ id: string; edit: boolean } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();
  const [keysOpen, setKeysOpen] = useState(false);
  const [allElements, setAllElements] = useState(false);
  const drawing = useRef<DrawingHandle>(null);
  const lastArea = useRef<"list" | "drawing">("list");
  const drawingDraft = useModelDraft(model, standards);
  const drawingDraftIndex = useMemo(() => indexElements(drawingDraft.draft), [drawingDraft.draft]);
  const drawingMemory = useDrawingMemory();
  const split = useSplit("lumberset.review.drawing-share", 0.6);

  const scrollToRow = (rowId: string, block: "nearest" | "center" = "nearest") =>
    requestAnimationFrame(() => reveal(document.getElementById(rowId), block, 48));

  /** Brings a row of the list into view, opening its part, and showing everything if a filter hid it. */
  const [revealing, setRevealing] = useState<{ row: string; seq: number } | null>(null);
  const reveals = useRef(0);
  const bringIntoView = (row: string) => {
    reveals.current += 1;
    setRevealing({ row, seq: reveals.current });
    const find = () =>
      document.querySelector<HTMLElement>(
        `[data-review-list] [data-review-row="${CSS.escape(row)}"]`,
      );
    requestAnimationFrame(() => {
      const node = find();
      if (node) {
        reveal(node, "center", 48);
        return;
      }
      setFilter("all");
      requestAnimationFrame(() => requestAnimationFrame(() => reveal(find(), "center", 48)));
    });
  };

  /** One Element chosen by itself, also one added on the drawing and not saved yet. */
  const oneElement = (elementId: string, extra: Partial<Focus>): Focus => {
    const entry = index.get(elementId) ?? drawingDraftIndex.get(elementId);
    return entry
      ? elementFocus(entry, extra)
      : { key: focusKey.element(elementId), title: elementId, elementIds: [elementId], ...extra };
  };

  /**
   * A box, a mark or a dot clicked on the drawing: the Element is chosen, and
   * the list opens where it is dealt with (its open flag, else the line it
   * carries most of), picked out there. When it belongs to what was chosen,
   * the rest of that stays drawn, so another click can step through a pile.
   */
  const chooseOnDrawing = (choice: { elementId: string; page: number; index: number }) => {
    const target = listTarget(choice.elementId, listIndex, { view, page: choice.page });
    if (target.kind === "line") setExpanded({ id: target.lineId, edit: false });
    setFocus(
      oneElement(choice.elementId, {
        elementIds: focus?.elementIds.includes(choice.elementId)
          ? focus.elementIds
          : [choice.elementId],
        box: { elementId: choice.elementId, index: choice.index },
        page: choice.page,
        row: target.row,
      }),
    );
    bringIntoView(target.row);
  };

  /** Opens a line, with an Element picked out in it when one is given. */
  const openLine = (lineId: string, elementId?: string) => {
    const line = takeoff.lines.find((candidate) => candidate.id === lineId);
    if (!line) return;
    setView("work");
    setExpanded({ id: line.id, edit: false });
    const row = focusKey.line(line.id);
    setFocus(
      elementId
        ? oneElement(elementId, {
            elementIds: lineFocus(line).elementIds,
            box: focus?.box?.elementId === elementId ? focus.box : { elementId, index: 0 },
            row,
          })
        : lineFocus(line),
    );
    bringIntoView(row);
  };

  const show = (next: Focus, look = false) => {
    setFocus(next);
    if (look && !desktop) setDrawerOpen(true);
  };

  /** Saves a draft; if the line open in the list became another Material, follows it. */
  const save = async (draft: BuildingModelInput, reason: CorrectionReason, note: string) => {
    const next = await work.saveModel(model, draft, reason, note);
    const open = expanded ? takeoff.lines.find((line) => line.id === expanded.id) : undefined;
    if (next && open && !next.lines.some((line) => line.id === open.id)) {
      // A taller wall takes longer studs: the line is now another Material. Follow it.
      const follow = next.lines.find(
        (candidate) =>
          candidate.section === open.section &&
          candidate.use === open.use &&
          candidate.elementIds.some((id) => open.elementIds.includes(id)),
      );
      if (follow) {
        setExpanded({ id: follow.id, edit: false });
        setFocus(lineFocus(follow));
        scrollToRow(`line-${follow.id}`);
      }
    }
    return next;
  };

  /** Work that has already said why it failed, in a toast, answers whether it worked. */
  const done = (work: Promise<unknown>) =>
    work.then(
      () => true,
      () => false,
    );
  const kit: ReviewKit = {
    model,
    standards,
    takeoff,
    index,
    by: "reviewer",
    canWork,
    saving: work.saving,
    people,
    settled,
    busy: work.busy,
    save: (draft, reason, note) => done(save(draft, reason, note)),
    settle: (flags, resolution, note) => done(work.settle(flags, resolution, note)),
    reopen: (flags) => done(work.reopen(flags)),
    focus,
    show,
    sheets,
    openLine,
  };

  const toggleCheck = (line: TakeoffLine, checked: boolean) => {
    if (!canWork) return;
    work.check.mutate({ lineId: line.id, checked, orderQuantity: line.orderQuantity });
  };
  const toggleExpand = (line: TakeoffLine, edit = false) => {
    setFocus(lineFocus(line));
    setExpanded((current) => (current?.id === line.id && !edit ? null : { id: line.id, edit }));
  };
  const showLine = (line: TakeoffLine) => {
    setView("work");
    setFilter("all");
    setFocus(lineFocus(line));
    setExpanded({ id: line.id, edit: false });
    setDrawerOpen(false);
    scrollToRow(`line-${line.id}`, "center");
  };

  /**
   * Next: what is left to review, in order. What needs the Reviewer, then the
   * assumptions, then every unchecked line in the order the list shows them.
   */
  const next = () => {
    const order = workOrder({
      needs: listIndex.needs,
      assumptions: listIndex.assumptions,
      lines: listedLines.map((line) => ({
        row: focusKey.line(line.id),
        checked: info.get(line.id)?.state === "checked",
      })),
    });
    const row = nextWork(order, focus?.row ?? focus?.key ?? null);
    if (!row) {
      toast.success("Nothing left to review", {
        description: "Everything that needs you is dealt with and every line is checked.",
      });
      return;
    }
    const nextFocus = focusOfRow(row);
    if (!nextFocus) return;
    setView("work");
    const chosen = focusKey.read(row);
    if (chosen.kind === "line") setExpanded({ id: chosen.id, edit: false });
    setFocus(nextFocus);
    bringIntoView(row);
  };

  /* ---- Keys ---- */

  /** What choosing a row of the list shows on the drawing. */
  const focusOfRow = (row: string): Focus | null => {
    const { kind, id: key } = focusKey.read(row);
    if (kind === "line") {
      const line = takeoff.lines.find((candidate) => candidate.id === key);
      return line ? lineFocus(line) : null;
    }
    if (kind === "sheet") {
      const page = Number(key);
      return {
        key: row,
        title: labelFor(page).long,
        elementIds: [],
        page,
      };
    }
    if (kind === "assumption") {
      const assumption = assumptions.find((item) => item.assumption.topic === key);
      return assumption
        ? {
            key: row,
            title: "Assumed for the whole job",
            elementIds: assumption.assumption.elementIds,
          }
        : null;
    }
    if (kind === "flag") {
      // A group's row stands for all of it: every wall without a height, every opening needing a header.
      const group = needs.find((candidate) => candidate.open[0]?.key === key);
      if (group && group.group.kind !== "flag") {
        const titles = {
          headers: "Openings that need an engineered header",
          wall_heights: "Walls with no height",
          counts: "Hardware with no count",
        } as const;
        return {
          key: row,
          title: titles[group.group.kind],
          elementIds: group.open.flatMap((item) => item.flag.elementIds),
        };
      }
      const item =
        needs.flatMap((candidate) => candidate.items).find((candidate) => candidate.key === key) ??
        notes.find((candidate) => candidate.key === key);
      return item ? flagFocus(item) : null;
    }
    return null;
  };

  const keys = useRef<(event: KeyboardEvent) => void>(() => undefined);
  keys.current = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        "input, textarea, select, [contenteditable=true], [role=alertdialog], [role=listbox], [role=menu], [role=combobox]",
      )
    ) {
      return;
    }
    // Keys work in the drawing's drawer on a phone, never in another dialog or the editor on the drawing.
    const dialog = target?.closest("[role=dialog]");
    if (dialog && !dialog.querySelector("[data-review-drawing]")) return;
    const pane = drawing.current;
    // The measuring tool in use takes its keys first: Enter keeps, Backspace takes a point off, a closes.
    if (pane?.toolKey(event.key)) {
      event.preventDefault();
      return;
    }
    const action = drawingKey(event);
    if (action) {
      if (action.kind === "next") {
        event.preventDefault();
        next();
        return;
      }
      // Up and down still scroll the list while the list is where the Reviewer works.
      if (action.kind === "pan" && action.dx === 0 && lastArea.current === "list" && !drawerOpen) {
        return;
      }
      if (pane?.act(action)) event.preventDefault();
      return;
    }
    switch (event.key) {
      case "?":
        event.preventDefault();
        setKeysOpen(true);
        return;
      case "Escape":
        if (pane?.escape()) event.preventDefault();
        else if (fullScreen) {
          event.preventDefault();
          setFullScreen(false);
        }
        return;
      case "d":
        if (canWork && pane?.draw()) event.preventDefault();
        return;
      case "f":
        if (desktop) {
          event.preventDefault();
          setFullScreen((value) => !value);
        }
        return;
      default:
        break;
    }
    if (event.key === "Enter" && lastArea.current === "drawing" && pane?.edit()) {
      event.preventDefault();
      return;
    }
    if (event.key === "j" || event.key === "k") {
      const rows = [
        ...document.querySelectorAll<HTMLElement>("[data-review-list] [data-review-row]"),
      ];
      if (rows.length === 0) return;
      event.preventDefault();
      lastArea.current = "list";
      const at = rows.findIndex((row) => row.dataset.reviewRow === (focus?.row ?? focus?.key));
      const next = rows[Math.min(Math.max(at + (event.key === "j" ? 1 : -1), 0), rows.length - 1)];
      const row = next?.dataset.reviewRow;
      const nextFocus = row ? focusOfRow(row) : null;
      if (next && nextFocus) {
        setFocus(nextFocus);
        requestAnimationFrame(() => reveal(next, "nearest", 48));
      }
      return;
    }
    const chosen = focus ? focusKey.read(focus.row ?? focus.key) : null;
    const line =
      chosen?.kind === "line"
        ? takeoff.lines.find((candidate) => candidate.id === chosen.id)
        : undefined;
    if (event.key === "x") {
      if (line) {
        event.preventDefault();
        toggleCheck(line, info.get(line.id)?.state !== "checked");
      } else if (chosen?.kind === "sheet" && canWork) {
        event.preventDefault();
        const page = Number(chosen.id);
        work.checkSheet.mutate({
          page,
          checked: !review.checkedSheets.some((check) => check.page === page),
        });
      }
      return;
    }
    if (!line) return;
    if (event.key === "Enter" && !target?.closest("button, a")) {
      event.preventDefault();
      toggleExpand(line);
    } else if (event.key === "e") {
      event.preventDefault();
      toggleExpand(line, true);
      scrollToRow(`line-${line.id}`);
    }
  };
  useEffect(() => {
    const listener = (event: KeyboardEvent) => keys.current(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  /* ---- Screen ---- */

  // A takeoff being read again says so above the list, in the header's band.
  const readOnlyNote =
    !canWork && !finished && staff && !reading
      ? review.assignedTo
        ? "Another Reviewer has this review. Take it over to check lines, settle flags and correct the takeoff."
        : "Claim this review to check lines, settle flags and correct the takeoff."
      : null;

  const list = (
    <ReviewList
      view={view}
      onView={setView}
      filter={filter}
      onFilter={setFilter}
      needs={needs}
      assumptions={assumptions}
      notes={notes}
      lines={listedLines}
      info={info}
      expandedId={expanded?.id ?? null}
      editFirst={expanded?.edit ?? false}
      onToggleExpand={(line) => toggleExpand(line)}
      onCheck={toggleCheck}
      sheets={sheets}
      sheetChecks={review.checkedSheets}
      onCheckSheet={(page, checked) => {
        if (canWork) work.checkSheet.mutate({ page, checked });
      }}
      readOnlyNote={readOnlyNote}
      onAllElements={() => setAllElements(true)}
      pageScroll={!desktop}
      riskFirst={riskFirst}
      onRiskFirst={setRiskFirst}
      revealing={revealing}
    />
  );

  /** Measurements and counts kept on the Review, and Sheets calibrated, by whoever may work on it. */
  const measureWork: MeasureWork = {
    save: (measurement) => work.saveMeasurement.mutate(measurement),
    remove: (measurementId) => work.removeMeasurement.mutate(measurementId),
    calibrate: (page, calibration) => work.calibrate.mutate({ page, calibration }),
  };

  const pane = (docked: boolean) => (
    <Drawing
      ref={drawing}
      sheets={sheets}
      draft={drawingDraft}
      lines={takeoff.lines}
      onShowLine={showLine}
      fullScreen={fullScreen}
      onFullScreen={desktop ? setFullScreen : undefined}
      docked={docked}
      className="h-full"
      memory={drawingMemory}
      states={states}
      onChoose={chooseOnDrawing}
      onNext={next}
      measurements={review.measurements}
      measureWork={canWork ? measureWork : undefined}
    />
  );

  const header = (
    <ReviewHeader
      data={data}
      progress={progress}
      sheets={sheetProgress}
      minutes={time.minutes}
      userId={user.id}
      assigneeName={review.assignedTo ? (people.get(review.assignedTo) ?? null) : null}
      canClaim={canClaim}
      canApprove={canWork}
      claiming={work.claim.isPending}
      onClaim={() => work.claim.mutate()}
      onApprove={() => setApproving(true)}
      onKeys={() => setKeysOpen(true)}
      onCloseOvertaken={canClose && data.newerRelease ? () => setClosing(true) : undefined}
      closeNote={
        canClose
          ? null
          : staff
            ? "Another Reviewer has it: take it over to close it."
            : "A Reviewer can close it."
      }
    />
  );

  const dialogs = (
    <>
      <ApprovePrompt
        open={approving}
        onOpenChange={setApproving}
        gated={review.mode === "gated"}
        progress={progress}
        approving={work.approve.isPending}
        onApprove={(approveNotes) =>
          work.approve.mutate(
            {
              notes: approveNotes,
              secondsSpent: time.seconds(),
            },
            {
              onSuccess: () => {
                setApproving(false);
                toast.success(
                  review.mode === "gated" ? "Approved and released to the workspace" : "Approved",
                  review.mode === "gated"
                    ? {
                        description:
                          "The workspace has been emailed. This is their Billable Quote.",
                      }
                    : undefined,
                );
              },
            },
          )
        }
      />
      <KeysPrompt open={keysOpen} onOpenChange={setKeysOpen} />
      {data.newerRelease ? (
        <CloseOvertakenPrompt
          open={closing}
          onOpenChange={setClosing}
          newer={data.newerRelease}
          closing={work.close.isPending}
          onClose={() =>
            work.close.mutate(undefined, {
              onSuccess: async () => {
                setClosing(false);
                toast.success("Review closed", {
                  description: "Its takeoff was canceled. Nobody was charged for it.",
                });
                await navigate({ to: "/" });
              },
            })
          }
        />
      ) : null}
      {/* Every Element by level, for what the list does not reach: a Level, the general notes. */}
      <CorrectionsEditor
        open={allElements}
        onOpenChange={setAllElements}
        buildingModel={model}
        standards={standards}
        by="reviewer"
        readOnly={!canWork}
        onSave={(body) => work.saveCorrections(body)}
        onShowEvidence={(evidence, label, elementId) => {
          setAllElements(false);
          const at = (index.get(elementId)?.element.evidence ?? []).findIndex(
            (item) =>
              item.page === evidence.page &&
              JSON.stringify(item.region ?? null) === JSON.stringify(evidence.region ?? null),
          );
          show(
            {
              key: focusKey.element(elementId),
              title: label,
              elementIds: [elementId],
              box: { elementId, index: Math.max(0, at) },
            },
            true,
          );
        }}
      />
    </>
  );

  if (!desktop) {
    return (
      <ReviewProvider kit={kit}>
        <div className="flex flex-col gap-y-3">
          {header}
          {/* Clipped, not hidden: a hidden overflow would stop the section headings sticking. */}
          <Container className="overflow-clip p-0">{list}</Container>
          {/* Sticky in the content column: fixed to the window, it covered the sidebar's
              account menu between the sidebar's breakpoint and this screen's desktop one. */}
          <Button
            size="base"
            className="pb-safe sticky bottom-4 z-20 self-start shadow-elevation-flyout"
            onClick={() => setDrawerOpen(true)}
          >
            <Photo /> Drawing
          </Button>
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <Drawer.Content
              className="h-[92dvh]"
              // Esc leaves a field being typed in first, as it does beside the list; then it closes
              // what is open on the drawing, a measurement in progress among it; the drawer last.
              onEscapeKeyDown={(event) => {
                const typing = (event.target as HTMLElement | null)?.closest<HTMLElement>(
                  "input, textarea, [contenteditable=true]",
                );
                if (typing) {
                  typing.blur();
                  event.preventDefault();
                  return;
                }
                if (drawing.current?.escape()) event.preventDefault();
              }}
            >
              <Drawer.Header className="py-3">
                <Drawer.Title className="h2-core">Drawing</Drawer.Title>
              </Drawer.Header>
              <div className="flex min-h-0 flex-1 flex-col">{pane(true)}</div>
            </Drawer.Content>
          </Drawer>
          {dialogs}
        </div>
      </ReviewProvider>
    );
  }

  return (
    <ReviewProvider kit={kit}>
      <div
        className={cn(
          "flex min-h-[560px] flex-col gap-y-3",
          fullScreen ? "fixed inset-0 z-40 bg-ui-bg-subtle p-2" : "h-[calc(100dvh-4.5rem)]",
        )}
      >
        {fullScreen ? null : header}
        <div ref={split.row} className="flex min-h-0 flex-1">
          <div
            onPointerDownCapture={() => {
              lastArea.current = "list";
            }}
            className="flex min-w-0 shrink-0 flex-col overflow-hidden rounded-lg bg-ui-bg-base shadow-elevation-card-rest"
            style={{ width: fullScreen ? "22rem" : `${(1 - split.share) * 100}%` }}
          >
            {list}
          </div>
          {fullScreen ? (
            <div className="w-2 shrink-0" />
          ) : (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Drag to give the drawing more or less room"
              aria-valuenow={Math.round(split.share * 100)}
              aria-valuemin={20}
              aria-valuemax={80}
              tabIndex={0}
              onPointerDown={split.onPointerDown}
              onDoubleClick={split.reset}
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                split.nudge(event.key === "ArrowLeft" ? 0.05 : -0.05);
              }}
              className="group flex w-3 shrink-0 cursor-col-resize touch-none items-center justify-center outline-none"
            >
              <span className="h-12 w-1 rounded-full bg-ui-border-strong transition-colors group-hover:bg-ui-fg-interactive group-focus-visible:bg-ui-fg-interactive" />
            </div>
          )}
          <div
            onPointerDownCapture={() => {
              lastArea.current = "drawing";
            }}
            className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-ui-bg-base shadow-elevation-card-rest"
          >
            {pane(false)}
          </div>
        </div>
      </div>
      {dialogs}
    </ReviewProvider>
  );
}
